import React, { FC } from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import Utils from '../../shared/utils';
import { CAMPAIGN_PRODUCTS, isFeatureEnabled } from '../../shared/utils/togglz';
import {
    concreteLoanFormatNameToKey,
    LOANFORMAT_ALL_OBJECT,
    LOANFORMAT_EAUDIOBOOKS,
    LOANFORMAT_EBOOKS
} from '../../store/loanFormat/actions';
import ProductCover from '../../shared/common/ProductCover';
import ProductSeries from '../../shared/product/ProductSeries';
import ProductAuthors from '../../shared/product/ProductAuthors';
import ProductNarrators from '../../shared/product/ProductNarrators';
import ProductLanguage from '../../shared/product/ProductLanguage';
import CopiesListInfoButton from '../../shared/product/CopiesListInfoButton';
import * as types from '../../store/constants';
import { PRODUCT_CLASS_CAMPAIGN } from '../../store/constants';
import ProductCampaignLabel from '../../shared/product/ProductCampaignLabel';
import ProductUsageConditions from '../../shared/product/ProductUsageConditions';
import { ProductListItemProps } from '../../shared/common/ProductList';
import { InvoiceOut } from '../../store/api';
import { SpocState } from '../../store/authentication/state';
import styled from 'styled-components';
import { LoanFormat, mapLoanFormat } from '../../../models/LoanFormat';
import { Trans, useTranslation } from 'react-i18next';
import FormatCurrency from '../../shared/utils/formatCurrency';

type Props = ProductListItemProps & {
    readonly loanFormat?: typeof LOANFORMAT_EBOOKS | typeof LOANFORMAT_EAUDIOBOOKS;
    readonly onDownloadInvoice: (invoice: InvoiceOut) => void;
};

const StSubscriptionPeriod = styled.div`
    margin-bottom: 8px;
`;

export function SubscriptionPeriodComponent({ startDate, endDate }: { startDate?: string; endDate?: string }) {
    const { t } = useTranslation();
    if (!startDate || !endDate) {
        return null;
    }

    const formatDate = (date: string) =>
        new Date(date).toLocaleDateString('en-AU', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });

    return (
        <StSubscriptionPeriod>
            {t('shop.orders.subscriptionPeriod')}
            {': '}
            <br />
            {formatDate(startDate)} - {formatDate(endDate)}
        </StSubscriptionPeriod>
    );
}

const CheckoutListItem: FC<Props> = props => {
    const { t } = useTranslation();
    const product = props.product;
    const { price } = product;
    const isCampaignProduct = product.productClass === PRODUCT_CLASS_CAMPAIGN;

    const packshotCssClass = classNames(
        'product-list-cell',
        'packshot',
        'checkout',
        'product',
        `${mapLoanFormat(product.loanFormat)?.toLowerCase()}-checkout`
    );

    const combinedTitle = product.title + (product.subTitle ? ` ${product.subTitle}` : '');

    const loanFormatKey = concreteLoanFormatNameToKey(product.loanFormat);

    return (
        <div className="product-list-row">
            <div className={packshotCssClass}>
                <Link to={`${types.PATH_PRODUCTS}/${product.id}`}>
                    <ProductCover minSizeInPx={180} product={product} />
                </Link>
            </div>
            <div className="tc product-list-cell tcw-30">
                {isCampaignProduct && isFeatureEnabled(CAMPAIGN_PRODUCTS) ? (
                    <ProductCampaignLabel loanFormat={mapLoanFormat(props.product.loanFormat)!} />
                ) : null}

                <ProductSeries {...props} loanFormatKey={loanFormatKey} />
                <p title={combinedTitle} className="label">
                    <Link to={`${types.PATH_PRODUCTS}/${product.id}`}>
                        <strong>{Utils.truncate(combinedTitle)}</strong>
                    </Link>
                </p>
                <ProductAuthors {...props} loanFormatKey={loanFormatKey} />
                <ProductNarrators {...props} loanFormatKey={loanFormatKey} />
                <p>
                    {t('components.productDetails.isbnWithLoanFormat', {
                        isbn: product.isbn13,
                        loanFormat: t(`loanFormat.plain.${mapLoanFormat(product.loanFormat)?.toLowerCase()}`, {
                            count: 1
                        })
                    })}
                </p>

                <ProductLanguage
                    product={product}
                    loanFormatKey={loanFormatKey}
                    label={product.language || ''}
                    shopLanguage={props.shopLanguage}
                />
            </div>

            <div className="tc product-list-cell tcw-15 a-right">
                {mapLoanFormat(product.loanFormat) === LoanFormat.ePress && product?.subscriptionPeriod ? (
                    <SubscriptionPeriodComponent
                        startDate={product.subscriptionPeriod.startDate}
                        endDate={product.subscriptionPeriod.endDate}
                    />
                ) : null}

                <ProductUsageConditions
                    product={product}
                    showDetails={true}
                    loanFormatKey={LOANFORMAT_ALL_OBJECT.key}
                    showSelectedPrice={true}
                    shopLanguage={props.shopLanguage}
                />
            </div>
            <div className="tc product-list-cell tcw-10 a-right">
                <p>
                    {t('numberFormat.numberWithCurrencySymbol', {
                        val: FormatCurrency.convertIntegerToCurrencyFormat(price?.copyBasePrice),
                        currencySymbol: FormatCurrency.mapCurrencyToSymbol(price?.currency)
                    })}
                </p>
            </div>
            <div className="tc product-list-cell tcw-5 a-right">
                <p>{t('numberFormat.numberWithoutDecimalPoint', { val: price?.copyAmount })}</p>
            </div>
            <div className="tc product-list-cell tcw-10 a-right">
                <div>
                    {(price?.savedValue ? '- ' : '') +
                        t('numberFormat.numberWithCurrencySymbol', {
                            val: FormatCurrency.convertIntegerToCurrencyFormat(price?.savedValue),
                            currencySymbol: FormatCurrency.mapCurrencyToSymbol(price?.currency)
                        })}
                </div>
                <div className="cold-grey">
                    {price?.savedPercentage
                        ? `(${t('numberFormat.numberWithoutDecimalPoint', {
                              val: Math.round((price?.savedPercentage ?? 0) / 100)
                          })}%)`
                        : null}
                </div>
            </div>
            <div className="tc product-list-cell tcw-10 a-right">
                <p>
                    {t('numberFormat.numberWithCurrencySymbol', {
                        val: FormatCurrency.convertIntegerToCurrencyFormat(price?.netValue),
                        currencySymbol: FormatCurrency.mapCurrencyToSymbol(price?.currency)
                    })}
                </p>
            </div>
            <div className="tc product-list-cell tcw-10 ">
                <CopiesListInfoButton
                    {...props}
                    label="Details"
                    className="fl-right"
                    styleBox={{ right: 0, left: 'initial' }}
                    styleArrow={{}}
                    disabled={true}
                    usageCondition={props.usageCondition}
                />
            </div>
        </div>
    );
};

type CheckoutListHeaderProps = {
    readonly spoc: SpocState;
};

export const CheckoutListHeader: FC<CheckoutListHeaderProps> = props => {
    const taxName = props.spoc.taxName || 'VAT';
    const { t } = useTranslation();
    return (
        <div className="product-list-container-new">
            <div className="product-list-row product-list-header">
                <div className="tc product-list-cell tcw-10">&nbsp;</div>
                <div className="tc product-list-cell tcw-30">&nbsp;</div>
                <div className="co-list-header tc product-list-cell tcw-15 a-right">
                    {t('shop.orders.usageCondition')}
                </div>
                <div className="co-list-header tc product-list-cell tcw-10 a-right">
                    <Trans
                        i18nKey="shop.orders.price"
                        values={{
                            taxName: taxName
                        }}
                        components={{
                            div: <div />
                        }}
                    />
                </div>
                <div className="co-list-header tc product-list-cell tcw-5 a-right">{t('shop.orders.quantity')}</div>
                <div className="co-list-header tc product-list-cell tcw-10 a-right">{t('shop.orders.discount')}</div>
                <div className="co-list-header tc product-list-cell tcw-10 a-right">
                    <Trans
                        i18nKey="shop.orders.netValue"
                        values={{
                            taxName: taxName
                        }}
                        components={{
                            div: <div />
                        }}
                    />
                </div>
                <div className="tc product-list-cell tcw-10">&nbsp;</div>
            </div>
        </div>
    );
};

export default CheckoutListItem;
